import { handleTokenExpiration, ReIssuanceAccessToken } from "../utils/auth";
import { handlePermissionErrors } from "../utils/errorHandler";

// === 동시성 제어용 전역 변수 ===
let isRefreshing = false;
let requestQueue = [];

// Utility to handle 401/T001 retry
const handleUnauthorizedAndRetry = async (originalFetch, args) => {
    const doRequest = async () => originalFetch(...args);
    let response = await doRequest();

    if (response.status === 401) {
        let responseBody = {};
        try {
            const contentType = response.headers.get("content-type") || "";
            if (contentType.includes("application/json")) {
                const text = await response.clone().text();
                if (text) {
                    responseBody = JSON.parse(text);
                }
            }
        } catch (e) {
            console.warn("JSON 파싱 실패 또는 응답 body 없음:", e);
        }

        const errorCode = responseBody?.code;

        if (errorCode === "T001") {
            // === 동시성 제어 ===
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    await ReIssuanceAccessToken();
                } catch (e) {
                    isRefreshing = false;
                    // 재발급 실패 시 대기열 모두 실패 처리
                    requestQueue.forEach(({ reject }) => reject(e));
                    requestQueue = [];
                    throw e;
                }
                isRefreshing = false;
                // 재발급 성공 시 대기열 모두 재시도
                const queued = [...requestQueue];
                requestQueue = [];
                for (const { resolve, reject, args: queuedArgs } of queued) {
                    try {
                        const retryResponse = await originalFetch(...queuedArgs);
                        resolve(retryResponse);
                    } catch (err) {
                        reject(err);
                    }
                }
                // 재발급 후 현재 요청 재시도
                return doRequest();
            } else {
                // 재발급 중이면 대기열에 추가하고 Promise로 대기
                return new Promise((resolve, reject) => {
                    requestQueue.push({ resolve, reject, args });
                });
            }
        } else if (errorCode === "T002") {
            await handleTokenExpiration();
        } else {
            console.warn("401 발생, 코드 없음 또는 처리 불가");
        }

        console.error(`HTTP error! status: ${response.status}`);
    }

    return response;
};

const GRAPHQL_URL = 'http://localhost:8080/graphql';
const REST_URL = 'http://localhost:8080';

const createFetch = (withAuth = true) => {
    const base = async (
        method,
        path,
        data,
        options
    ) => {
        const headers = {
            "Content-Type": "application/json",
        };

        const fetchOptions = {
            method,
            headers,
            credentials: withAuth ? "include" : "same-origin",
            ...options,
        };

        if (data && method !== "GET") {
            fetchOptions.body = JSON.stringify(data);
        }

        const queryString = method === "GET" && data
            ? `?${new URLSearchParams(data).toString()}`
            : "";

        const relativeUrl = `${path}${queryString}`;
        const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        const fullUrl = isLocalhost ? REST_URL + relativeUrl : relativeUrl;

        return handleUnauthorizedAndRetry(fetch, [fullUrl, fetchOptions]);
    };

    return {
        get: (path, params, options) => base("GET", path, params, options),
        post: (path, body, options) => base("POST", path, body, options),
        put: (path, body, options) => base("PUT", path, body, options),
        patch: (path, body, options) => base("PATCH", path, body, options),
        delete: (path, body, options) => base("DELETE", path, body, options),
    };
};

export const dontLoginFetch = createFetch(true);
export const apiFetch = createFetch(true);
export const initialFetch = async (
    method,
    path,
    data,
    customHeaders,
    options
) => {
    const defaultHeaders = {
        "Content-Type": "application/json",
    };

    const fetchOptions = {
        method,
        headers: customHeaders || defaultHeaders,
        credentials: "include",
        ...options,
    };

    if (data && method !== "GET") {
        fetchOptions.body = JSON.stringify(data);
    }

    const queryString = method === "GET" && data
        ? `?${new URLSearchParams(data).toString()}`
        : "";

    const relativeUrl = `${path}${queryString}`;
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const fullUrl = isLocalhost ? REST_URL + relativeUrl : relativeUrl;

    return handleUnauthorizedAndRetry(fetch, [fullUrl, fetchOptions]);
};

// query, mutation 모두 동작함
export const graphFetch = async (
    body, // query || mutation
    variables,
    options
) => {
    const fetchOptions = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ query: body, variables }),
        ...options,
    };

    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const url = isLocalhost ? GRAPHQL_URL : "/graphql";

    const handledResponse = await handleUnauthorizedAndRetry(fetch, [url, fetchOptions]);
    const json = await handledResponse.json();

    if (json.errors && json.errors.length > 0) {
        // 권한 에러 체크 및 처리
        const hasPermissionError = await handlePermissionErrors(json.errors);
        
        if (hasPermissionError) {
            return null;
        }

        const errorMessage = json.errors.map(error => error.message).join('\n');
        
        console.error(errorMessage);

        const error = new Error(errorMessage);
        error.response = json;
        throw error;
    }

    return json.data;
};